import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  useTheme,
  alpha,
  Chip,
  SelectChangeEvent,
} from "@mui/material";
import { AccountTree, SubdirectoryArrowRight, People } from "@mui/icons-material";
import { useTranslate } from "@refinedev/core";
import { SubGroup } from "../../hooks/useSubGroups";

interface SubGroupSelectorProps {
  subGroups: SubGroup[];
  selectedSubGroupId: number | null;
  onSubGroupChange: (subGroupId: number | null) => void;
  disabled?: boolean;
}

interface HierarchicalOption {
  id: number;
  name: string;
  depth: number;
  memberCount: number;
}

export const SubGroupSelector: React.FC<SubGroupSelectorProps> = ({
  subGroups,
  selectedSubGroupId,
  onSubGroupChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const translate = useTranslate();

  // Build hierarchical options (same pattern as CompanyShow.tsx)
  const hierarchicalOptions = useMemo(() => {
    const buildOptions = (
      groups: SubGroup[],
      parentId: number | null = null,
      depth = 0
    ): HierarchicalOption[] => {
      const result: HierarchicalOption[] = [];
      const children = groups.filter((g) => g.parentId === parentId);

      for (const child of children) {
        result.push({
          id: child.id,
          name: child.name,
          depth,
          memberCount: child._count?.users || 0,
        });
        result.push(...buildOptions(groups, child.id, depth + 1));
      }

      return result;
    };

    return buildOptions(subGroups);
  }, [subGroups]);

  const selectedSubGroup = useMemo(() => {
    return subGroups.find((sg) => sg.id === selectedSubGroupId);
  }, [subGroups, selectedSubGroupId]);

  const handleChange = (event: SelectChangeEvent<number | string>) => {
    const value = event.target.value;
    onSubGroupChange(value === "" ? null : Number(value));
  };

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <Select
        value={selectedSubGroupId || ""}
        onChange={handleChange}
        displayEmpty
        sx={{
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
          "& .MuiSelect-select": {
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
        }}
        renderValue={(value) => {
          if (!value || !selectedSubGroup) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                <AccountTree fontSize="small" />
                <Typography variant="body2">
                  {translate("subGroups.dnd.selectGroup", "Sélectionner un sous-groupe")}
                </Typography>
              </Box>
            );
          }

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountTree fontSize="small" sx={{ color: "primary.main" }} />
              <Typography variant="body2" fontWeight={500}>
                {selectedSubGroup.name}
              </Typography>
              <Chip
                icon={<People sx={{ fontSize: 14 }} />}
                label={selectedSubGroup._count?.users || 0}
                size="small"
                sx={{
                  height: 22,
                  ml: "auto",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "& .MuiChip-label": {
                    px: 0.75,
                    fontSize: 12,
                  },
                  "& .MuiChip-icon": {
                    ml: 0.5,
                    color: "primary.main",
                  },
                }}
              />
            </Box>
          );
        }}
      >
        <MenuItem value="" disabled>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {translate("subGroups.dnd.selectGroup", "Sélectionner un sous-groupe")}
          </Typography>
        </MenuItem>

        {hierarchicalOptions.map((option) => (
          <MenuItem
            key={option.id}
            value={option.id}
            sx={{
              py: 1,
              "&.Mui-selected": {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              {/* Indentation */}
              {option.depth > 0 && (
                <Box
                  sx={{
                    width: option.depth * 20,
                    display: "flex",
                    justifyContent: "flex-end",
                    mr: 0.5,
                  }}
                >
                  <SubdirectoryArrowRight
                    fontSize="small"
                    sx={{ color: "text.disabled", fontSize: 16 }}
                  />
                </Box>
              )}

              {/* Icon */}
              <AccountTree
                fontSize="small"
                sx={{
                  mr: 1,
                  color: option.depth === 0 ? "primary.main" : "text.secondary",
                  fontSize: 18,
                }}
              />

              {/* Name */}
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontWeight: option.depth === 0 ? 500 : 400,
                }}
              >
                {option.name}
              </Typography>

              {/* Member count */}
              <Chip
                size="small"
                label={option.memberCount}
                sx={{
                  height: 20,
                  minWidth: 28,
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  "& .MuiChip-label": {
                    px: 0.75,
                    fontSize: 11,
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SubGroupSelector;
